import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import { fileUrl } from "@/components/UploadButton";

const TOPIC_OPTIONS = [
  "Charcuterie boards",
  "Cheese / serving platters",
  "Coasters & small pieces",
  "Live-edge wood + resin",
  "Coloring & pigment techniques",
  "Live-edge furniture",
];

export default function Workshops() {
  const { content } = useContent();
  const w = content.workshops || {};

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    experience_level: "beginner",
    topics: [],
    preferred_format: "in_person",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleTopic = (t) => {
    setForm((f) => ({
      ...f,
      topics: f.topics.includes(t) ? f.topics.filter((x) => x !== t) : [...f.topics, t],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/workshop-interests", form);
      setDone(true);
      toast.success("You're on the list. We'll be in touch.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="workshop-success">
        <p className="smallcaps mb-4">You&apos;re In</p>
        <h1 className="font-display text-5xl text-[#212121] mb-6">Thanks for the interest.</h1>
        <p className="text-[#757575] max-w-xl mx-auto">
          We&apos;ll email you as soon as workshop dates are confirmed — and you&apos;ll be among the first to grab a spot.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="workshops-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-16">
      <div>
        {w.image && (
          <img src={fileUrl(w.image)} alt="" className="w-full aspect-[4/5] object-cover border border-[#B2DFDB] mb-8" />
        )}
        {w.kicker && <p className="smallcaps mb-3">{w.kicker}</p>}
        {w.heading && (
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#212121] mb-6 leading-tight">{w.heading}</h1>
        )}
        {w.subtext && <p className="text-[#757575] leading-relaxed">{w.subtext}</p>}

        <div className="mt-10 border-t border-[#B2DFDB] pt-8 space-y-4 text-sm text-[#757575]">
          <div className="flex gap-4">
            <span className="smallcaps min-w-[8rem]">Format</span>
            <span>Small groups (4–8 people), full day, lunch included</span>
          </div>
          <div className="flex gap-4">
            <span className="smallcaps min-w-[8rem]">Location</span>
            <span>The workshop — shaped by demand from the waitlist</span>
          </div>
          <div className="flex gap-4">
            <span className="smallcaps min-w-[8rem]">You take home</span>
            <span>The piece you make, plus the materials list to keep going</span>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6 lg:pt-12" data-testid="workshop-form">
        <p className="smallcaps">Register your interest</p>
        <h2 className="font-display text-3xl text-[#212121]">Join the waitlist</h2>
        <p className="text-[#757575] text-sm">
          No commitment — we&apos;ll just send you a heads-up when dates are confirmed.
        </p>

        <div className="grid sm:grid-cols-2 gap-6 pt-2">
          <div>
            <label className="smallcaps block mb-2">Your Name</label>
            <input data-testid="wi-name" required className="input-light" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label className="smallcaps block mb-2">Email</label>
            <input data-testid="wi-email" type="email" required className="input-light" value={form.email} onChange={set("email")} />
          </div>
        </div>
        <div>
          <label className="smallcaps block mb-2">Phone (optional)</label>
          <input data-testid="wi-phone" className="input-light" value={form.phone} onChange={set("phone")} />
        </div>

        <div>
          <label className="smallcaps block mb-3">Experience with resin</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "beginner", l: "Total beginner" },
              { v: "some", l: "Some" },
              { v: "experienced", l: "Experienced" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setForm({ ...form, experience_level: o.v })}
                data-testid={`wi-exp-${o.v}`}
                className={`border p-3 text-xs uppercase tracking-wider transition-colors ${
                  form.experience_level === o.v
                    ? "border-[#B87333] text-[#B87333] font-bold bg-[#FFFFFF]"
                    : "border-[#B2DFDB] text-[#757575] hover:border-[#B87333]"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="smallcaps block mb-3">What interests you? (pick any)</label>
          <div className="flex flex-wrap gap-2">
            {TOPIC_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleTopic(t)}
                data-testid={`wi-topic-${t.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                  form.topics.includes(t)
                    ? "border-[#B87333] text-[#B87333] font-bold bg-[#FFFFFF]"
                    : "border-[#B2DFDB] text-[#757575] hover:border-[#B87333]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="smallcaps block mb-3">Preferred format</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "in_person", l: "In person" },
              { v: "virtual", l: "Virtual" },
              { v: "either", l: "Either" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setForm({ ...form, preferred_format: o.v })}
                data-testid={`wi-format-${o.v}`}
                className={`border p-3 text-xs uppercase tracking-wider transition-colors ${
                  form.preferred_format === o.v
                    ? "border-[#B87333] text-[#B87333] font-bold bg-[#FFFFFF]"
                    : "border-[#B2DFDB] text-[#757575] hover:border-[#B87333]"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="smallcaps block mb-2">Anything else?</label>
          <textarea data-testid="wi-notes" rows={3} className="input-light" placeholder="Specific questions, group bookings, dates that work…" value={form.notes} onChange={set("notes")} />
        </div>

        <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="wi-submit">
          {submitting ? "Sending…" : "Join the waitlist"}
        </button>
      </form>
    </div>
  );
}
